import { HttpContextToken, HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { Injector, inject } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { KeycloakService } from 'keycloak-angular';
import { SwalService } from '../services/utils/swal.service';
import { withCurrentLocale } from '../services/utils/keycloak-locale';
import { SKIP_AUTH } from './token.interceptor';

/**
 * Marque une requête dont l'échec est pris en charge par son appelant, sans
 * rien imposer à l'écran.
 *
 * <p>Le cas réel est une information secondaire : l'écran de résultat demande
 * si le test peut être repassé, après avoir affiché la copie. Si le serveur est
 * injoignable à ce moment-là, la fenêtre d'erreur générale recouvrait une note
 * pourtant bien affichée, pour une réponse dont l'écran sait se passer.</p>
 *
 * <p>Distinct de {@link SKIP_AUTH}, qui retire en plus le jeton Keycloak : la
 * requête silencieuse reste une requête authentifiée vers l'API.</p>
 *
 * <p>Ce qui est tu : la fenêtre (réseau, 5xx, action refusée) et la redirection d'un 403. Ce
 * qui ne l'est pas : la reconnexion quand la session est réellement perdue —
 * elle concerne toute l'application, pas cette seule requête. L'erreur est
 * toujours journalisée et relayée à l'appelant.</p>
 */
export const SILENT_ERRORS = new HttpContextToken<boolean>(() => false);

/**
 * Traitement centralisé des erreurs HTTP.
 *
 * Le projet n'avait aucune stratégie globale : chaque composant gérait (ou pas)
 * son `error:`, et le pattern dominant était d'avaler l'erreur pour afficher des
 * données fictives. Cet intercepteur garantit qu'une erreur est toujours visible
 * quelque part, puis **relaie l'erreur** pour que le composant puisse afficher
 * son propre `<app-error-state>`.
 *
 * Répartition des responsabilités :
 *  - intercepteur  : erreurs transverses (401, 403, 0, 5xx) + journalisation
 *
 * <p><b>Un 403 ne dit pas la même chose selon le geste.</b> Une <i>lecture</i>
 * refusée, c'est un écran qu'on n'a pas le droit d'ouvrir : on en sort, vers
 * `/unauthorized`. Une <i>écriture</i> refusée, c'est un bouton qu'on n'avait
 * pas le droit de presser sur un écran qu'on a le droit de lire — depuis que
 * les droits distinguent consultation et modification, c'est un cas ordinaire.
 * Éjecter ferait perdre l'écran et la saisie pour un refus qui se dit en une
 * phrase : le message s'affiche sur place. La règle tient en une ligne — seule
 * la méthode GET redirige.</p>
 *  - composant     : erreurs métier (404 sur une ressource, 409, 422) via son
 *                    propre état d'erreur dans l'UI
 */
export const errorInterceptor: HttpInterceptorFn = (req, next) => {
  const router = inject(Router);
  const keycloak = inject(KeycloakService);

  // `SwalService` et `TranslateService` sont résolus à l'erreur, pas au montage
  // de la chaîne — sans quoi ils la referment sur elle-même :
  //
  //   SwalService → TranslateService → HttpClient → errorInterceptor → SwalService
  //
  // Le cycle est latent tant que rien ne construit `SwalService` pendant que
  // la chaîne se monte. Il mord dès que le démarrage emprunte le chemin
  // d'erreur — API injoignable, session refusée — et Angular rend alors un
  // `NG0200` et une page blanche, en lieu et place du message d'erreur que
  // l'intercepteur existe précisément pour afficher.
  //
  // L'intercepteur n'a besoin des deux qu'en cas d'échec : les demander plus
  // tôt ne servait qu'à créer la boucle.
  const injector = inject(Injector);

  return next(req).pipe(
    catchError((error: HttpErrorResponse) => {
      // Requête hors API (dépôt présigné vers le stockage) : ses codes ne
      // parlent pas de la session Keycloak. Un 403 y signifie « signature
      // expirée », typiquement au bout d'un transfert de plusieurs Go — le
      // traiter comme un défaut d'autorisation renverrait le professeur sur
      // /unauthorized au lieu de lui montrer l'échec de son envoi.
      if (req.context.get(SKIP_AUTH)) {
        return throwError(() => error);
      }

      const swal = injector.get(SwalService);
      const translate = injector.get(TranslateService);

      const silencieuse = req.context.get(SILENT_ERRORS);

      switch (error.status) {
        // Réseau injoignable, CORS, certificat localhost non approuvé.
        case 0:
          if (!silencieuse) swal.error(translate.instant('ERRORS.NETWORK'));
          break;

        // 401 : ne relancer un login QUE si la session est réellement perdue.
        // Un 401 alors qu'on est authentifié = autorisation insuffisante sur la
        // ressource (pas une session expirée) ; relancer keycloak.login()
        // reviendrait sur la même page, refirerait la requête, re-401… boucle
        // infinie. Dans ce cas on relaie l'erreur au composant.
        // Pas d'exception pour une requête silencieuse : une session perdue
        // l'est pour toute l'application.
        case 401:
          if (!keycloak.getKeycloakInstance()?.authenticated) {
            keycloak.login(withCurrentLocale({ redirectUri: window.location.href }));
          }
          break;

        case 403:
          if (silencieuse) break;
          if (req.method === 'GET') router.navigate(['/unauthorized']);
          else swal.error(translate.instant('ERRORS.FORBIDDEN_ACTION'));
          break;

        case 500:
        case 502:
        case 503:
        case 504:
          if (!silencieuse) swal.error(translate.instant('ERRORS.SERVER'));
          break;

        // 400 / 404 / 409 / 422 : erreurs métier, laissées au composant qui a
        // le contexte pour produire un message utile.
        default:
          break;
      }

      console.error(
        `[HTTP ${error.status}] ${req.method} ${req.urlWithParams}`,
        error.error,
      );

      return throwError(() => error);
    }),
  );
};
